<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { api, messageFrom } from '../api'
import type { Generation, ModelRole, ModelRow, Theme } from '../types'

interface TrialRun { id: string; status: string; generations: Array<Generation & { model_title: string }> }

const models = ref<ModelRow[]>([])
const themes = ref<Theme[]>([])
const selectedModels = ref<string[]>([])
const presetCode = ref('')
const photo = ref<File | null>(null)
const run = ref<TrialRun | null>(null)
const busy = ref(false)
const loading = ref(true)
const error = ref('')
let pollTimer: number | null = null

const presets = computed(() => themes.value.flatMap(theme => theme.subthemes.flatMap(item => item.presets)))

function rub(value: string | number | null): string {
  return value === null ? '—' : `${(Number(value) * 80).toFixed(2)} ₽`
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const [modelResponse, catalogResponse] = await Promise.all([
      api<{ models: ModelRow[] }>('/api/admin/models'),
      api<{ themes: Theme[] }>('/api/admin/catalog'),
    ])
    models.value = modelResponse.models
    themes.value = catalogResponse.themes
    presetCode.value ||= presets.value[0]?.code ?? ''
    selectedModels.value = models.value.filter(model => model.is_active).map(model => model.code)
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function saveModel(model: ModelRow): Promise<void> {
  error.value = ''
  try {
    const response = await api<{ model: ModelRow }>(`/api/admin/models/${encodeURIComponent(model.code)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role: model.role, isActive: model.is_active }),
    })
    const index = models.value.findIndex(item => item.code === model.code)
    if (index >= 0) models.value[index] = response.model
    if (response.model.role === 'free' || response.model.role === 'paid') {
      for (const other of models.value) {
        if (other.code !== response.model.code && other.role === response.model.role) other.role = 'off'
      }
    }
  } catch (reason) {
    error.value = messageFrom(reason)
    await load()
  }
}

function roleChanged(model: ModelRow, event: Event): void {
  model.role = (event.target as HTMLSelectElement).value as ModelRole
  if (model.role !== 'off') model.is_active = true
  void saveModel(model)
}

function activeChanged(model: ModelRow, event: Event): void {
  model.is_active = (event.target as HTMLInputElement).checked
  if (!model.is_active) model.role = 'off'
  void saveModel(model)
}

function chooseFile(event: Event): void {
  photo.value = (event.target as HTMLInputElement).files?.[0] ?? null
}

function toggleModel(code: string, checked: boolean): void {
  selectedModels.value = checked
    ? [...new Set([...selectedModels.value, code])]
    : selectedModels.value.filter(item => item !== code)
}

async function poll(runId: string): Promise<void> {
  try {
    const response = await api<{ run: TrialRun }>(`/api/admin/trials/${runId}`)
    run.value = response.run
    if (response.run.status === 'processing') pollTimer = window.setTimeout(() => void poll(runId), 1_500)
  } catch (reason) { error.value = messageFrom(reason) }
}

async function startTrial(): Promise<void> {
  if (!photo.value || !presetCode.value || !selectedModels.value.length || busy.value) return
  busy.value = true
  error.value = ''
  run.value = null
  if (pollTimer !== null) window.clearTimeout(pollTimer)
  try {
    const body = new FormData()
    body.append('photo', photo.value)
    body.append('presetCode', presetCode.value)
    body.append('modelCodes', JSON.stringify(selectedModels.value))
    const response = await api<{ runId: string }>('/api/admin/trials', { method: 'POST', body })
    await poll(response.runId)
  } catch (reason) { error.value = messageFrom(reason) }
  finally { busy.value = false }
}

onMounted(() => void load())
onBeforeUnmount(() => { if (pollTimer !== null) window.clearTimeout(pollTimer) })
</script>

<template>
  <main class="page">
    <div class="page-title">
      <div><p class="eyebrow">Настройка качества</p><h2>Модели</h2></div>
      <button class="button button--quiet" type="button" @click="load">Обновить</button>
    </div>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="muted">Загружаю модели…</p>
    <div v-else class="table-wrap">
      <table>
        <thead><tr><th>Модель</th><th>Цена</th><th>Время</th><th>Качество</th><th>Роль</th><th>Вкл.</th></tr></thead>
        <tbody>
          <tr v-for="model in models" :key="model.code">
            <td><strong>{{ model.title }}</strong><small>{{ model.provider_model }}</small></td>
            <td>{{ rub(model.price_usd) }}<small>${{ Number(model.price_usd).toFixed(4) }}</small></td>
            <td>≈ {{ model.avg_seconds }} с</td>
            <td>{{ model.quality_note }}</td>
            <td>
              <select :value="model.role" @change="roleChanged(model, $event)">
                <option value="free">Бесплатная</option>
                <option value="paid">Платная</option>
                <option value="off">Выключена</option>
              </select>
            </td>
            <td><input type="checkbox" :checked="model.is_active" @change="activeChanged(model, $event)" /></td>
          </tr>
        </tbody>
      </table>
    </div>

    <section class="panel trial-panel">
      <div><p class="eyebrow">Главный инструмент</p><h3>Проба моделей</h3></div>
      <p class="muted">Одно фото и один пресет пройдут через выбранные модели в общей очереди (до четырёх одновременно).</p>
      <div class="form-grid">
        <label>Пресет
          <select v-model="presetCode">
            <optgroup v-for="theme in themes" :key="theme.code" :label="`${theme.emoji} ${theme.title}`">
              <option v-for="preset in theme.subthemes.flatMap(item => item.presets)" :key="preset.code" :value="preset.code">
                {{ preset.emoji }} {{ preset.title }}
              </option>
            </optgroup>
          </select>
        </label>
        <label>Фото<input type="file" accept="image/jpeg,image/png,image/webp" @change="chooseFile" /></label>
      </div>
      <div class="check-list">
        <label v-for="model in models" :key="model.code" :class="{ muted: !model.is_active }">
          <input
            type="checkbox" :checked="selectedModels.includes(model.code)" :disabled="!model.is_active"
            @change="toggleModel(model.code, ($event.target as HTMLInputElement).checked)"
          /> {{ model.title }} · {{ rub(model.price_usd) }} ожидаемо
        </label>
      </div>
      <button
        class="button button--primary" type="button"
        :disabled="busy || !photo || !presetCode || !selectedModels.length" @click="startTrial"
      >{{ busy ? 'Ставлю в очередь…' : 'Проба' }}</button>
    </section>

    <section v-if="run" class="trial-results">
      <article v-for="item in run.generations" :key="item.id" class="result-card">
        <div class="result-image">
          <img v-if="item.status === 'completed'" :src="`/media/generations/${item.id}`" :alt="item.model_title" />
          <span v-else-if="item.status === 'failed' || item.status === 'blocked_nsfw'">Не получилось</span>
          <span v-else>Рисую…</span>
        </div>
        <h4>{{ item.model_title }}</h4>
        <p><strong>{{ rub(item.cost_usd) }}</strong> · {{ item.cost_usd === null ? 'цена появится после ответа' : `$${Number(item.cost_usd).toFixed(4)}` }}</p>
        <p>{{ item.runtime_ms === null ? 'в очереди' : `${(item.runtime_ms / 1000).toFixed(1)} с` }}</p>
        <p v-if="item.error" class="error">{{ item.error }}</p>
      </article>
    </section>
  </main>
</template>
